import React, { useState, useEffect } from "react";
import sideCloseIcon from "../../../../assets/SideCloseIcon.svg";
import { usePopupContext } from "../../../../hooks/UsePopupContext";
import { getQuestionByLessonService } from "../../../../services/quizSystem/GetQuestionService";
import { getLegacyLessonQuiz } from "../../../../services/quizSystem/GetLegacyLessonQuizService";
import { startQuizAttempt, submitQuizAnswer, completeQuizAttempt } from "../../../../services/quiz/quizAttempt";
import QuizPopup from "./QuizPopup";
import { useNavigate } from "react-router-dom";
import MusicQuizCard from "../../quizesSceen/components/MusicQuizCard";
import "../../quizesSceen/components/MusicQuizCard.css";
import crossIcon from "../../../../assets/wrong-icon.png";
import checkIcon from "../../../../assets/check-icon.png";
import "../styles/QuizPopup.css";

// You can replace this with an imported SVG if you have one
const CrossIcon = () => (
  <img
    src={crossIcon}
    alt="Cross Icon"
    style={{
      width: 22,
      height: 22,
      display: 'flex',
      alignItems: 'center',
    }}
  />
);

const CheckIcon = () => (
  <img
    src={checkIcon}
    alt="Check Icon"
    style={{
      width: 22,
      height: 22,
      display: 'flex',
      alignItems: 'center',
    }}
  />
);

export default function LessonQuizPopup(props) {
  const { dispatch3 } = usePopupContext();
  const navigate = useNavigate();
  
  // Quiz state
  const [quizes, setQuizes] = useState(null);
  const [legacyQuiz, setLegacyQuiz] = useState(null);
  const [quizAttemptId, setQuizAttemptId] = useState(null);
  const [answerStartTimes, setAnswerStartTimes] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [allQuestionsAnswered, setAllQuestionsAnswered] = useState(false);
  const [selected, setSelected] = useState([]);
  const [showAnswer, setShowAnswer] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  
  const quizzesPerPage = 5;

  const fetchQuizesByLesson = async () => {
    try {
      const originalQuiz = await getLegacyLessonQuiz(props.videoID);
      if (originalQuiz?.questions?.length && originalQuiz?.answers?.length) {
        setLegacyQuiz(originalQuiz);
        return;
      }

      const response = await getQuestionByLessonService(props.videoID);
      if (Array.isArray(response)) {
        // Transform API response to match UI's expected structure
        const transformed = response.map(q => {
          const options = q.questionOptions?.map(opt => opt.optionText) || [];
          const correctIndex = q.questionOptions?.findIndex(opt => opt.isCorrect);
          
          return {
            id: q.id,
            question: q.questionText,
            options,
            correctIndex,
            questionText: q.questionText,
            questionType: q.questionType,
            correctAnswer: q.correctAnswer || q.questionOptions?.[correctIndex]?.optionText || "",
            pianoFromNote: q.pianoFromNote || null,
            pianoToNote: q.pianoToNote || null,
            highlightedKey: q.highlightedKey || null,
            imgURL: q.imgURL || null,
            questionOptions: q.questionOptions || []
          };
        });
        setQuizes(transformed);
        setSelected(Array(transformed.length).fill(null));
        setShowAnswer(Array(transformed.length).fill(false));
        
        // Initialize answer start times for all questions
        const startTimes = {};
        transformed.forEach(q => {
          startTimes[q.id] = Date.now();
        });
        setAnswerStartTimes(startTimes);
        
        // Start the quiz attempt
        if (transformed.length > 0) {
          startQuizAttemptHandler(props.videoID);
        }
      }
    } catch (error) {
      console.error("Error fetching quiz questions by lesson:", error);
    }
  };

  // Start quiz attempt
  const startQuizAttemptHandler = async (lessonId) => {
    try {
      // Identity now comes solely from the JWT on the server side - no client-supplied id.
      const response = await startQuizAttempt(null, lessonId);
      if (response) {
        setQuizAttemptId(response.id);
      }
    } catch (error) {
      console.error("Error starting quiz attempt:", error);
    }
  };

  useEffect(() => {
    if (props.videoID) {
      fetchQuizesByLesson();
    }
  }, [props.videoID]);

  // Use API data if available, otherwise fallback to empty array
  const quizData = quizes || [];
  const totalPages = Math.ceil(quizData.length / quizzesPerPage);
  const startIdx = (currentPage - 1) * quizzesPerPage;
  const endIdx = startIdx + quizzesPerPage;
  const quizzesToShow = quizData.slice(startIdx, endIdx);

  const handleSelect = async (quizIdx, optIdx, passedIsCorrect = null) => {
    const newSelected = [...selected];
    newSelected[quizIdx] = optIdx;
    setSelected(newSelected);
    const newShow = [...showAnswer];
    newShow[quizIdx] = true;
    setShowAnswer(newShow);
    
    // Check if all questions are answered
    const allAnswered = newSelected.every(answer => answer !== null);
    setAllQuestionsAnswered(allAnswered);
    
    // Submit the answer to the API
    if (quizAttemptId && quizes && quizes[quizIdx]) {
      const quiz = quizes[quizIdx];
      const questionId = quiz.id;
      let userAnswer;
      
      if (quiz.questionType === "TEXT_ANSWER") {
        userAnswer = optIdx; // For text answers, optIdx contains the actual text
      } else if (quiz.options && quiz.options.length > 0) {
        userAnswer = quiz.options[optIdx];
      } else {
        userAnswer = optIdx.toString();
      }
      
      // Calculate answer time in seconds
      const startTime = answerStartTimes[questionId] || Date.now();
      const answerTimeSeconds = Math.round((Date.now() - startTime) / 1000);
      
      // Determine if the answer is correct
      const isCorrect = passedIsCorrect !== null ? passedIsCorrect : (
        quiz.questionType === "TEXT_ANSWER"
          ? optIdx.trim().toLowerCase() === quiz.correctAnswer.trim().toLowerCase()
          : quiz.questionType === "TRUE_FALSE"
            ? (quiz.correctAnswer.toLowerCase() === "true" ? optIdx === 0 : optIdx === 1)
            : optIdx === quiz.correctIndex
      );
      
      // Submit the answer
      try {
        await submitQuizAnswer(quizAttemptId, questionId, userAnswer, answerTimeSeconds, isCorrect);
        
        // Update start time for next question
        const newStartTimes = { ...answerStartTimes };
        newStartTimes[questionId] = Date.now();
        setAnswerStartTimes(newStartTimes);
      } catch (error) {
        console.error("Error submitting answer:", error);
      }
    }
  };

  // Handle quiz submission
  const handleSubmitQuiz = async () => {
    if (!quizAttemptId) return;
    
    setIsSubmitting(true);
    try {
      const result = await completeQuizAttempt(quizAttemptId);
      if (result) {
        // Close the popup and show result
        dispatch3({ type: "POPUPFALSE" });
        // You can navigate to a result page or show result in a modal
        console.log("Quiz completed:", result);
      }
    } catch (error) {
      console.error("Error completing quiz:", error);
      setIsSubmitting(false);
    }
  };

  if (legacyQuiz) {
    return <QuizPopup {...props} quizData={legacyQuiz} />;
  }

  return (
    <div className='QuizPopupWrapper'>
      <div className='QuizPopupHeader'>
        <h3>Lesson Quiz</h3>
        <img
          src={sideCloseIcon}
          alt='sideCloseIcon'
          onClick={() => {
            dispatch3({ type: "POPUPFALSE" });
          }}
        />
      </div>
      
      <div style={{ padding: "20px" }}>
        {quizzesToShow.length === 0 ? (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <h3>No quiz questions available for this lesson.</h3>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 60 }}>
            {quizzesToShow.map((quiz, idx) => (
              <QuizCard
                key={startIdx + idx}
                quiz={quiz}
                idx={startIdx + idx}
                selected={selected[startIdx + idx]}
                showAnswer={showAnswer[startIdx + idx]}
                onSelect={handleSelect}
              />
            ))}
          </div>
        )}
        
        {/* Submit Quiz Button - Only shown on the last page when all questions are answered */}
        {currentPage === totalPages && quizData.length > 0 && (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 40, marginBottom: 40 }}>
            <button
              onClick={handleSubmitQuiz}
              disabled={isSubmitting}
              style={{
                padding: "12px 32px",
                borderRadius: 24,
                background: "#1976d2",
                color: "#fff",
                fontWeight: 600,
                fontSize: 16,
                border: "none",
                cursor: isSubmitting ? "not-allowed" : "pointer",
                opacity: isSubmitting ? 0.7 : 1,
                boxShadow: "0 2px 8px rgba(25, 118, 210, 0.3)"
              }}
            >
              {isSubmitting ? "Submitting..." : "Submit Quiz"}
            </button>
          </div>
        )}
        
        {totalPages > 1 && (
          <div
            className="pagination-controls"
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 8,
              marginBottom: 40,
              marginTop: 20,
              background: "#e6f0fd",
              borderRadius: 24,
              padding: "16px 24px",
              width: "fit-content",
              marginLeft: "auto",
              marginRight: "auto",
            }}
          >
            {/* Left Arrow */}
            <button
              style={{
                background: currentPage === 1 ? "#cfe2f7" : "#1976d2",
                border: "none",
                borderRadius: "50%",
                width: 36,
                height: 36,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: currentPage === 1 ? "#a0b8d9" : "#fff",
                fontSize: 20,
                cursor: currentPage === 1 ? "not-allowed" : "pointer",
                marginRight: 8,
                transition: "background 0.2s",
              }}
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(currentPage - 1)}
            >
              {'\u25C0'}
            </button>

            {/* Page Numbers */}
            {[...Array(totalPages)].map((_, i) => (
              <button
                key={i}
                style={{
                  background: currentPage === i + 1 ? "#0a1a4f" : "#fff",
                  color: currentPage === i + 1 ? "#fff" : "#0a1a4f",
                  border: "none",
                  borderRadius: "50%",
                  width: 32,
                  height: 32,
                  fontWeight: 600,
                  margin: "0 4px",
                  fontSize: 16,
                  boxShadow: "0 1px 4px #0001",
                  cursor: "pointer",
                  transition: "background 0.2s, color 0.2s",
                }}
                onClick={() => setCurrentPage(i + 1)}
              >
                {i + 1}
              </button>
            ))}

            {/* Right Arrow */}
            <button
              style={{
                background: currentPage === totalPages ? "#cfe2f7" : "#1976d2",
                border: "none",
                borderRadius: "50%",
                width: 36,
                height: 36,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: currentPage === totalPages ? "#a0b8d9" : "#fff",
                fontSize: 20,
                cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                marginLeft: 8,
                transition: "background 0.2s",
              }}
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(currentPage + 1)}
            >
              {'\u25B6'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function QuizCard({ quiz, idx, selected, showAnswer, onSelect }) {
  // Determine if the answer is correct or wrong
  const isCorrect = selected === quiz.correctIndex;
  const isWrong = showAnswer && selected !== null && selected !== quiz.correctIndex;

  // For TEXT_ANSWER type
  const [textAnswer, setTextAnswer] = useState("");
  const [textAnswered, setTextAnswered] = useState(false);
  const [textCorrect, setTextCorrect] = useState(false);

  const handleTextSubmit = () => {
    setTextAnswered(true);
    setTextCorrect(textAnswer.trim().toLowerCase() === quiz.correctAnswer.trim().toLowerCase());
  };

  // Handle music quiz question types
  const isMusicQuiz = [
    "TEXT_NOTE_TO_PIANO",
    "PIANO_TO_TEXT_NOTE",
    "SYMBOL_NOTE_TO_PIANO",
    "PIANO_TO_SYMBOL_NOTE",
    "IMAGE_CHOICE"
  ].includes(quiz.questionType);

  if (isMusicQuiz) {
    return (
      <MusicQuizCard 
        quiz={quiz} 
        idx={idx} 
        onCorrectAnswer={(idx, userAnswer, answerTimeSeconds, isCorrect) => {
          // For music quizzes, we pass the isCorrect parameter directly from the MusicQuizCard
          onSelect(idx, userAnswer, isCorrect);
        }}
      />
    );
  }

  if (quiz.questionType === "TEXT_ANSWER") {
    return (
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 500, fontSize: 20, lineHeight: '100%', letterSpacing: 0, marginBottom: 5, color: '#000000', textAlign: 'left' }}>
          {idx + 1}. {quiz.question}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginTop: 8 }}>
            <input
              type="text"
              value={textAnswer}
              onChange={e => setTextAnswer(e.target.value)}
              disabled={textAnswered}
              placeholder="Type answer here"
              style={{
                width: 220,
                height: 36,
                border: '1px solid #ccc',
                borderRadius: 18,
                fontSize: 16,
                padding: '0 12px',
                marginRight: 8,
                outline: 'none',
                background: textAnswered ? '#f5f5f5' : '#fff',
              }}
            />
            {!textAnswered && (
              <button
                onClick={handleTextSubmit}
                style={{
                  height: 36,
                  borderRadius: 18,
                  background: '#1976d2',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: 14,
                  border: 'none',
                  padding: '0 18px',
                  cursor: 'pointer',
                  marginLeft: 0,
                }}
              >
                Submit
              </button>
            )}
          </div>
        </div>
        {textAnswered && (
          <div style={{ color: textCorrect ? "#1ca15f" : "#d32f2f", fontWeight: 600, fontSize: 15, marginTop: 16, textAlign: 'left' }}>
            {textCorrect ? (
              <>Correct Answer</>
            ) : (
              <>Wrong Answer. Correct Answer: {quiz.correctAnswer}</>
            )}
          </div>
        )}
      </div>
    );
  }

  if (quiz.questionType === "TRUE_FALSE") {
    // For TRUE_FALSE questions, create two buttons for True and False
    const options = ["True", "False"];
    const correctIndex = quiz.correctAnswer.toLowerCase() === "true" ? 0 : 1;
    
    return (
      <div style={{ marginBottom: 20, minHeight: 10 }}>
        <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 500, fontSize: 20, lineHeight: '100%', letterSpacing: 0, marginBottom: 5, color: '#000000', textAlign: 'left' }}>
          {idx + 1}. {quiz.question}
        </div>
        <div style={{ display: "flex", gap: 30, flexWrap: "wrap", marginBottom: 40 }}>
          {options.map((opt, optIdx) => {
            let border = "none";
            let boxShadow = "0 1px 4px #0001";
            let icon = null;
            if (showAnswer && selected === optIdx) {
              if (optIdx === correctIndex) {
                border = "2px solid #1ca15f";
                boxShadow = "0 0 0 2px #1ca15f33";
                icon = <CheckIcon />;
              } else {
                border = "2px solid #d32f2f";
                boxShadow = "0 0 0 2px #d32f2f33";
                icon = <CrossIcon />;
              }
            }
            return (
              <button
                key={opt}
                onClick={() => onSelect(idx, optIdx)}
                disabled={showAnswer}
                style={{
                  width: 350,
                  height: 40,
                  padding: 18,
                  borderRadius: 24,
                  border,
                  background: "#fff",
                  color: '#222',
                  fontWeight: 500,
                  fontSize: 20,
                  marginBottom: -40,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  cursor: showAnswer ? "default" : "pointer",
                  outline: "none",
                  boxShadow,
                  position: 'relative',
                  transition: 'border 0.2s',
                }}
              >
                <span style={{ flex: 1, textAlign: 'left' }}>{String.fromCharCode(65 + optIdx)}. {opt}</span>
                {icon && <div style={{ marginLeft: 8, flexShrink: 0 }}>{icon}</div>}
              </button>
            );
          })}
        </div>
        {showAnswer && (
          <div style={{ color: selected === correctIndex ? "#1ca15f" : "#d32f2f", fontWeight: 600, fontSize: 15, marginTop: 50, marginBottom: 20, textAlign: 'left' }}>
            {selected === correctIndex ? (
              <>Correct Answer : {String.fromCharCode(65 + correctIndex)}</>
            ) : (
              <>Wrong Answer. Correct Answer : {String.fromCharCode(65 + correctIndex)}</>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ marginBottom: 8, minHeight: 10 }}>
      <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 500, fontSize: 20, lineHeight: '100%', letterSpacing: 0, marginBottom: 5, color: '#000000', textAlign: 'left' }}>
        {idx + 1}. {quiz.question}
      </div>
      <div style={{ display: "flex", gap: 30, flexWrap: "wrap", marginBottom: 40 }}>
        {quiz.options.map((opt, optIdx) => {
          let border = "none";
          let boxShadow = "0 1px 4px #0001";
          let icon = null;
          if (showAnswer && selected === optIdx) {
            if (optIdx === quiz.correctIndex) {
              border = "2px solid #1ca15f";
              boxShadow = "0 0 0 2px #1ca15f33";
              icon = <CheckIcon />;
            } else {
              border = "2px solid #d32f2f";
              boxShadow = "0 0 0 2px #d32f2f33";
              icon = <CrossIcon />;
            }
          }
          return (
            <button
              key={opt}
              onClick={() => onSelect(idx, optIdx)}
              disabled={showAnswer}
              style={{
                width: 350,
                height: 40,
                padding: 18,
                borderRadius: 24,
                border,
                background: "#fff",
                color: '#222',
                fontWeight: 500,
                fontSize: 20,
                marginBottom: -40,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                cursor: showAnswer ? "default" : "pointer",
                outline: "none",
                boxShadow,
                position: 'relative',
                transition: 'border 0.2s',
              }}
            >
              <span style={{ flex: 1, textAlign: 'left' }}>{String.fromCharCode(65 + optIdx)}. {opt}</span>
              {icon && <div style={{ marginLeft: 8, flexShrink: 0 }}>{icon}</div>}
            </button>
          );
        })}
      </div>
      {showAnswer && (
        <div style={{ color: isCorrect ? "#1ca15f" : "#d32f2f", fontWeight: 600, fontSize: 15, marginTop: 80, marginBottom: 20, textAlign: 'left' }}>
          {isCorrect ? (
            <>Correct Answer : {String.fromCharCode(65 + quiz.correctIndex)}</>
          ) : (
            <>Wrong Answer. Correct Answer : {String.fromCharCode(65 + quiz.correctIndex)}</>
          )}
        </div>
      )}
    </div>
  );
} 
